import { useEffect, useState } from "react";
import { Search, Loader2, ClipboardList, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export const cardCls = "rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80";
export const blueBtn = "bg-[#1565c0] text-white hover:bg-[#0d47a1]";

export function MPage({ children, className }) {
  return <main className={cn("space-y-4 px-4 py-4 pb-32", className)}>{children}</main>;
}

export function Footer({ children, className, testId }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-md" data-testid={testId}>
      <div className={cn("pb-[env(safe-area-inset-bottom)]", className)}>{children}</div>
    </div>
  );
}

export function Fab({ onClick, label, testId }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 mx-auto flex max-w-md justify-end px-5">
      <button onClick={onClick} aria-label={label} data-testid={testId}
        className="pointer-events-auto grid h-16 w-16 place-items-center rounded-full bg-[#1565c0] text-white shadow-xl shadow-blue-900/30 transition-transform hover:bg-[#0d47a1] active:scale-95">
        <Plus className="h-8 w-8" />
      </button>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, testId, icon: Icon = Search, inputMode, onEnter }) {
  return (
    <label className="flex h-12 items-center gap-2 rounded-xl bg-white px-3 shadow-sm ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-[#1565c0]">
      <Icon className="h-5 w-5 shrink-0 text-slate-400" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} inputMode={inputMode}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); } }}
        className="h-full w-full bg-transparent text-base outline-none placeholder:text-slate-400" data-testid={testId} />
      {value && <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-slate-400" data-testid={`${testId}-clear`}>Limpar</button>}
    </label>
  );
}

export function EmptyArt({ title, text, icon: Icon = ClipboardList, plus = false, testId = "m-empty" }) {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center fade-up" data-testid={testId}>
      <div className="relative mb-6 h-40 w-40">
        <span className="absolute inset-0 rounded-full bg-[#e3f2fd]" />
        <span className="absolute left-2 top-6 h-4 w-4 rounded-full bg-[#90caf9]" />
        <span className="absolute right-5 top-2 h-2.5 w-2.5 rounded-full bg-[#1565c0]/60" />
        <span className="absolute bottom-8 left-0 h-3 w-3 rounded-full bg-amber-300" />
        <div className="absolute inset-8 grid rotate-[-6deg] place-items-center rounded-3xl bg-white shadow-lg">
          <Icon className="h-14 w-14 text-[#1565c0]" />
        </div>
        {plus && <span className="absolute -bottom-1 right-5 grid h-11 w-11 place-items-center rounded-full bg-[#1565c0] text-white shadow-lg"><Plus className="h-6 w-6" /></span>}
      </div>
      <p className="font-display text-lg font-semibold text-slate-800">{title}</p>
      {text && <p className="mt-1 max-w-xs text-sm text-slate-500">{text}</p>}
    </div>
  );
}

const TONES = {
  blue: "bg-[#e3f2fd] text-[#0d47a1]", green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-700", slate: "bg-slate-100 text-slate-600",
};
export function Chip({ tone = "slate", children, testId }) {
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", TONES[tone])} data-testid={testId}>{children}</span>;
}

export function MTabs({ value, onChange, items, testId = "m-tabs" }) {
  return (
    <div className="flex gap-1 rounded-xl bg-slate-200/70 p-1" data-testid={testId}>
      {items.map(([k, label, n]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
          className={cn("flex-1 rounded-lg px-2 py-2 text-sm font-semibold transition-colors", value === k ? "bg-white text-[#0d47a1] shadow-sm" : "text-slate-500")}>
          {label}{n ? <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] text-white">{n}</span> : null}
        </button>
      ))}
    </div>
  );
}

export const Loading = () => <div className="grid place-items-center py-16" data-testid="m-loading"><Loader2 className="h-7 w-7 animate-spin text-[#1565c0]" /></div>;

export function ReasonDialog({ open, title, description, label = "Motivo", required = false, confirmText = "Confirmar", onClose, onConfirm, testId = "m-reason" }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(""); }, [open]);
  const ok = async () => { setBusy(true); try { await onConfirm(v.trim()); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <label className="space-y-1 text-sm">
          <span className="font-medium">{label}{required ? " *" : " (opcional)"}</span>
          <Textarea value={v} onChange={(e) => setV(e.target.value)} rows={3} maxLength={200} className="text-base" data-testid={`${testId}-input`} />
        </label>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Voltar</Button>
          <Button onClick={ok} className={blueBtn} disabled={busy || (required && !v.trim())} data-testid={`${testId}-confirm`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{confirmText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
